import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { MarketplaceOrderingAvailableService } from '@core/services/marketplace-ordering/marketplace-ordering-available.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertModalComponent } from '@shared/modals/alert-modal/alert-modal.component';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, take, takeUntil, tap } from 'rxjs/operators';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { ForceOrderingAvailableService } from '@core/services/marketplace-ordering/force-ordering-available.service';
import { canUseForceOrderingBypass } from '@core/services/marketplace-ordering/force-ordering-available.util';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';

@Injectable({
  providedIn: 'root',
})
export class MarketplaceOrderingUnavailableModalService {
  private readonly modalsService = inject(ModalsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly marketplaceOrderingAvailableService = inject(
    MarketplaceOrderingAvailableService,
  );
  private readonly modalSelector = inject(ModalStoreSelector);
  private readonly settingsSelector = inject(SettingsStoreSelector);
  private readonly router = inject(Router);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly translateService = inject(TranslateService);
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  private readonly forceOrderingAvailableService = inject(ForceOrderingAvailableService);
  private readonly linkNavigationService = inject(LinkNavigationService);

  private destroy$ = new Subject<void>();
  private lastOrderingUnavailableState: boolean | null = null;
  private wasModalDismissed = false;
  private isPollingRegistered = false;
  private pendingSplashContinueUrl: string | null = null;
  private suppressHomeRedirectOnClose = false;

  start(): void {
    if (this.destroy$.closed) {
      this.destroy$ = new Subject<void>();
    }
    this.syncPollingWithKioskLayout();
    this.watchOrderingAvailability();
    this.listenToModalClose();
  }

  stop(): void {
    this.setPollingRegistered(false);
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
    this.lastOrderingUnavailableState = null;
    this.wasModalDismissed = false;
  }

  /** Only poll ordering-available while kiosk layout is active. */
  private syncPollingWithKioskLayout(): void {
    this.settingsSelector.selectKiosk
      .pipe(
        map(kiosk => kiosk.isKioskLayout),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        tap(isKioskLayout => this.setPollingRegistered(isKioskLayout)),
      )
      .subscribe();
  }

  private setPollingRegistered(registered: boolean): void {
    if (registered === this.isPollingRegistered) {
      return;
    }
    this.isPollingRegistered = registered;
    if (registered) {
      this.marketplaceOrderingAvailableService.register();
    } else {
      this.marketplaceOrderingAvailableService.unregister();
    }
  }

  /** Whether the user can leave the kiosk splash (marketplace ordering available). */
  canProceedFromSplash$(continueUrl?: string): Observable<boolean> {
    return this.marketplaceOrderingAvailableService.marketplaceContext$.pipe(
      take(1),
      tap(({ ordering_available }) => {
        if (!ordering_available) {
          this.pendingSplashContinueUrl = continueUrl ?? null;
          this.openModal();
        }
      }),
      map(({ ordering_available }) => ordering_available),
    );
  }

  /** Dev-only: bypass ordering-available and optionally continue splash navigation. */
  continueWithDevBypass(): void {
    if (!canUseForceOrderingBypass()) {
      return;
    }
    this.suppressHomeRedirectOnClose = true;
    this.forceOrderingAvailableService.enableDevSessionBypass();
    this.modalsService.close(AlertModalComponent.handle);
    const continueUrl = this.pendingSplashContinueUrl;
    this.pendingSplashContinueUrl = null;
    if (continueUrl) {
      this.linkNavigationService.navigate(continueUrl);
    }
    this.wasModalDismissed = false;
  }

  private openModal(): void {
    const { title, message } = this.getModalCopy();
    const data: { title: string; message: string; kioskOrderingDevBypass?: boolean } = {
      title,
      message,
    };
    if (canUseForceOrderingBypass()) {
      data.kioskOrderingDevBypass = true;
    }
    this.modalsService.open(AlertModalComponent.handle, undefined, data);
    this.logModalOpen();
  }

  private logModalOpen(): void {
    this.analyticsService.log({
      level: 'info',
      label: 'MarketplaceOrderingUnavailableModalService - openModal',
      message: {
        from: 'MarketplaceOrderingUnavailableModalService.openModal',
        url: this.router.url,
        ordering_available: false,
      },
    });
  }

  private listenToModalClose(): void {
    this.modalSelector
      .selectModal(AlertModalComponent.handle)
      .pipe(
        takeUntil(this.destroy$),
        pairwise(),
        filter(([previous, current]) => !!previous && !current),
        filter(() => this.settingsSelector.isKioskLayout()),
        tap(() => {
          if (this.suppressHomeRedirectOnClose) {
            this.suppressHomeRedirectOnClose = false;
            return;
          }
          this.pendingSplashContinueUrl = null;
          this.wasModalDismissed = true;
          this.router.navigate([this.routerHelper.translateRoute('/')]);
        }),
      )
      .subscribe();
  }

  private isOnHomeOrSplash(): boolean {
    const homeUrl = this.routerHelper.translateRoute('/');
    const currentUrl = this.router.url.split('?')[0];
    return (
      currentUrl === '/' ||
      currentUrl === homeUrl ||
      /^\/[a-z]{2}\/?$/.test(currentUrl) ||
      currentUrl.endsWith('/kiosk-splash-screen')
    );
  }

  private watchOrderingAvailability(): void {
    this.marketplaceOrderingAvailableService.marketplaceContext$
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.settingsSelector.isKioskLayout()),
        distinctUntilChanged(
          (previous, current) => previous.ordering_available === current.ordering_available,
        ),
        tap(({ ordering_available }) => {
          const isOrderingUnavailable = !ordering_available;

          const shouldOpenModal =
            isOrderingUnavailable &&
            (this.lastOrderingUnavailableState === false ||
              this.lastOrderingUnavailableState === null ||
              this.wasModalDismissed);

          if (shouldOpenModal && !this.isOnHomeOrSplash()) {
            this.openModal();
            this.wasModalDismissed = false;
          }

          if (!isOrderingUnavailable && this.lastOrderingUnavailableState === true) {
            this.modalsService.close(AlertModalComponent.handle);
            this.wasModalDismissed = false;
          }

          this.lastOrderingUnavailableState = isOrderingUnavailable;
        }),
      )
      .subscribe();
  }

  private getModalCopy(): { title: string; message: string } {
    const custom = this.kioskGlobalSettingsService.settings().orderingUnavailableModal;
    const defaultTitle = this.translateService.instant('shop-page.schedules.closed');
    const defaultMessage = this.translateService.instant(
      'shop-page.schedules.kiosk-closed-message',
    );
    const title = custom?.title?.trim() ? custom.title.trim() : defaultTitle;
    const message = custom?.message?.trim() ? custom.message.trim() : defaultMessage;
    return { title, message };
  }
}
